import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import Breadcrumb from '@/components/Breadcrumb';
import SEO from '@/components/SEO';

const FAQ = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Tümü');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch('/api/faq-content-public');
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  if (!content) return null;

  const categories = ['Tümü', ...new Set(content.faqs.map(faq => faq.category))];
  const filteredFaqs = selectedCategory === 'Tümü' 
    ? content.faqs 
    : content.faqs.filter(faq => faq.category === selectedCategory);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
    <SEO 
      title="Sık Sorulan Sorular"
      description="MİGA Yapı hakkında sık sorulan sorular. Kentsel dönüşüm süreci, maliyetler, garanti, ödeme planları ve daha fazlası hakkında bilgi alın."
      keywords="kentsel dönüşüm sık sorulan sorular, inşaat faq, miga yapı sss, kentsel dönüşüm süreci"
    />
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Sık Sorulan Sorular' }]} />

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="heading-font text-5xl sm:text-6xl font-bold text-[#1e4a7a] mb-6">
            {content.title}
          </h1>
          <div className="w-24 h-1 bg-[#dc2626] mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-[#dc2626] text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {filteredFaqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <span className="text-sm text-[#dc2626] font-medium mb-1 block">
                    {faq.category}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {faq.question}
                  </h3>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-500 transition-transform duration-300 flex-shrink-0 ml-4 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`transition-all duration-300 ease-in-out ${
                  openIndex === index
                    ? 'max-h-96 opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5 pt-2">
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 bg-gradient-to-r from-[#1e4a7a] to-[#2563eb] rounded-3xl p-8 text-center text-white">
          <h2 className="heading-font text-3xl font-bold mb-4">
            Sorunuzun cevabını bulamadınız mı?
          </h2>
          <p className="text-lg mb-6">
            Bizimle iletişime geçin, size yardımcı olmaktan memnuniyet duyarız.
          </p>
          <a
            href="/iletisim"
            className="inline-block bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            İletişime Geçin
          </a>
        </div>
      </div>
    </div>
    </>
  );
};

export default FAQ;
